import { ReactNode } from 'react';

import { useIsFieldRecentlyModified } from '@/hooks/use-is-field-recently-modified';

interface TableCellWithRecentlyModifiedProps {
  /** Product ID to check for recent modifications */
  productId: string;
  /** Field name to check for recent modifications */
  field: string;
  /** Whether this is a parent row (parent rows should not be highlighted) */
  isParent: boolean;
  /** Render function that receives isRecentlyModified boolean */
  children: (isRecentlyModified: boolean) => ReactNode;
}

/**
 * Wrapper component that checks if a field is recently modified and passes
 * the result to its children via a render prop.
 *
 * This component is defined outside the cell render function to allow React
 * to memoize and reuse it efficiently, improving performance in large tables.
 *
 * @example
 * ```tsx
 * <TableCellWithRecentlyModified
 *   productId={row.original.id}
 *   field="buyingPrice"
 *   isParent={isParent}
 * >
 *   {(isRecentlyModified) => (
 *     <TablePriceDisplay isRecentlyModified={isRecentlyModified} />
 *   )}
 * </TableCellWithRecentlyModified>
 * ```
 */
export function TableCellWithRecentlyModified({
  productId,
  field,
  isParent,
  children,
}: TableCellWithRecentlyModifiedProps) {
  const isRecentlyModifiedHook = useIsFieldRecentlyModified(productId, field);
  const isRecentlyModified = !isParent && isRecentlyModifiedHook;
  return <>{children(isRecentlyModified)}</>;
}
